import { useEffect, useRef } from "react"
import { api } from "@/lib/api"
import { decryptField, encryptField, looksLikeCiphertext } from "@/lib/crypto"
import { sweepPatch } from "@/lib/private-mode-sweep"
import { useCryptoStore } from "@/store/crypto"

// The sweep encrypts plaintext rows left over from standard mode. It runs only
// after private mode is first set up on this browser (requestEncryptionBackfill),
// never on a plain unlock: on a later unlock or a new device, plaintext on the
// server may have been planted, and encrypting it would make it look genuine.
// Such rows stay visible with a "not encrypted" flag (decryptTransactionRows).
const BACKFILL_PENDING_KEY = "paisa_encbf_pending"

/** Call once when private mode is first set up, so the next unlock sweeps leftovers. */
export function requestEncryptionBackfill() {
  try {
    localStorage.setItem(BACKFILL_PENDING_KEY, "1")
  } catch {
    // storage unavailable: leftovers stay visible, flagged "not encrypted"
  }
}

/** The sweep running in this tab, if any (awaited by stopEncryptionBackfill). */
let backfillRun: Promise<void> | null = null

/**
 * Before turning Private mode off: cancels the leftovers sweep and waits for a write already on its way, so
 * nothing lands after the decrypt pass read that row. Returns whether a sweep was pending, so a failed
 * Turn off can put it back with requestEncryptionBackfill (Dobby review, #1111).
 */
export async function stopEncryptionBackfill(): Promise<boolean> {
  let was = false
  try {
    was = localStorage.getItem(BACKFILL_PENDING_KEY) !== null
  } catch {
    // storage unavailable: nothing pending
  }
  cancelEncryptionBackfill()
  await backfillRun?.catch(() => {})
  return was
}

/** Private mode was turned off: a pending sweep must not re-encrypt the decrypted rows. */
export function cancelEncryptionBackfill() {
  try {
    localStorage.removeItem(BACKFILL_PENDING_KEY)
  } catch {
    // storage unavailable: nothing pending
  }
}

type TxnRow = {
  id: string
  description: string
  referenceNumber: string | null
  notes: string | null
}

async function encryptIfPlaintext(
  value: string | null | undefined,
  key: CryptoKey,
): Promise<string | undefined> {
  if (!value) return undefined
  try {
    await decryptField(value, key)
    return undefined // already encrypted with this key — skip
  } catch {
    // Decryption failed. If the value looks like valid AES-GCM ciphertext
    // (correct base64 format, ≥28 decoded bytes), it was likely encrypted
    // with a different key — skip rather than double-encrypt it.
    if (looksLikeCiphertext(value)) return undefined
    return encryptField(value, key) // plaintext — encrypt it
  }
}

export function useBackfillEncryption() {
  const { key, mode } = useCryptoStore()
  const running = useRef(false)

  useEffect(() => {
    if (mode !== "private" || !key || running.current) return
    if (!localStorage.getItem(BACKFILL_PENDING_KEY)) return

    const capturedKey = key
    running.current = true

    // Still wanted for this key: turning off (this tab clears the flag first, then the key) or another
    // device turning it off or re-keying (checked with the server every 25 rows) stops the sweep, so no
    // row is encrypted after the household is back in standard mode (Dobby review, #1111).
    let sinceCheck = 0
    async function wanted() {
      if (!stillHere()) return false
      if (++sinceCheck < 25) return true
      sinceCheck = 0
      const cur = await api.get<{ encryptionMode?: string }>("/auth-crypto")
      return cur.encryptionMode === "private"
    }

    const stillHere = () =>
      localStorage.getItem(BACKFILL_PENDING_KEY) !== null &&
      useCryptoStore.getState().key === capturedKey

    async function sweep() {
      let cursor: string | null | undefined
      do {
        const url: string = cursor
          ? `/transactions?limit=200&cursor=${cursor}`
          : "/transactions?limit=200"
        const res: { data: TxnRow[]; nextCursor?: string | null } = await api.get(url)

        for (const txn of res.data) {
          const patches: Record<string, string | null> = {}

          const desc = await encryptIfPlaintext(txn.description, capturedKey)
          if (desc !== undefined) patches.description = desc

          const ref = await encryptIfPlaintext(txn.referenceNumber, capturedKey)
          if (ref !== undefined) patches.referenceNumber = ref

          const notes = await encryptIfPlaintext(txn.notes, capturedKey)
          if (notes !== undefined) patches.notes = notes

          if (Object.keys(patches).length > 0) {
            if (!(await wanted())) return
            await sweepPatch(`/transactions/${txn.id}`, patches, api, stillHere)
          }
        }

        cursor = res.nextCursor
      } while (cursor)

      localStorage.removeItem(BACKFILL_PENDING_KEY)
    }

    backfillRun = sweep()
      .catch(() => {
        running.current = false // allow retry on next unlock
      })
      .finally(() => {
        backfillRun = null
        // A cancelled sweep ends normally: free the guard, or a flag a failed Turn off puts back would wait
        // for a page reload instead of the next unlock (a finished sweep left no flag, so it won't rerun).
        running.current = false
      })
  }, [key, mode])
}
